import React from "react";
import { motion } from "framer-motion";
import { IMAGES } from "@/assets/images";
import { COMPANY_INFO, HERO_DATA } from "@/data/index";
import { CONTACT_INFO } from "@/lib/index";
import { 
  MapPin, 
  Target, 
  ShieldCheck, 
  Users, 
  Award, 
  ChevronRight,
  History
} from "lucide-react";

/**
 * Page À Propos de YKE Multi_Services
 * Met en valeur l'histoire, la vision et l'ancrage local à SIPIM Port-Bouët
 */
export default function About() {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  return (
    <div className="flex flex-col w-full">
      {/* Hero Section */}
      <section className="relative py-20 bg-background overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-10">
          <img 
            src={IMAGES.LAND_DEVELOPMENT_1} 
            alt="Abidjan Background" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="max-w-3xl"
          >
            <h1 className="text-4xl md:text-6xl font-heading mb-6 text-foreground">
              À Propos de <span className="text-primary">YKE Multi_Services</span>
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed font-sans">
              {COMPANY_INFO.slogan} — Excellence, Intégrité et Prestige au cœur de la Côte d'Ivoire.
            </p>
          </motion.div>
        </div>
      </section>

      {/* History & Mission Section */}
      <section className="py-24 bg-card">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative aspect-square rounded-2xl overflow-hidden shadow-2xl border border-primary/10"
            >
              <img 
                src={IMAGES.BUSINESS_TEAM_2} 
                alt="L'équipe YKE Multi_Services" 
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-black/80 to-transparent text-white">
                <p className="font-medium italic">"Nous bâtissons des relations basées sur la confiance et l'excellence."</p>
              </div>
            </motion.div>

            <motion.div 
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="flex flex-col space-y-8"
            >
              <div className="space-y-4">
                <div className="flex items-center gap-3 text-primary">
                  <History className="w-6 h-6" />
                  <span className="uppercase tracking-widest text-sm font-bold">Notre Histoire</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-heading text-foreground">Une référence de prestige à Port-Bouët</h2>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  {COMPANY_INFO.history}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-6 rounded-xl bg-background border border-border hover:border-primary/50 transition-colors">
                  <Target className="w-10 h-10 text-primary mb-4" />
                  <h3 className="text-xl font-bold mb-2">Notre Vision</h3>
                  <p className="text-sm text-muted-foreground">{COMPANY_INFO.vision}</p>
                </div>
                <div className="p-6 rounded-xl bg-background border border-border hover:border-primary/50 transition-colors">
                  <Award className="w-10 h-10 text-primary mb-4" />
                  <h3 className="text-xl font-bold mb-2">Nos Valeurs</h3>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {COMPANY_INFO.values.map((val) => (
                      <li key={val} className="flex items-center gap-2">
                        <ChevronRight className="w-3 h-3 text-primary" />
                        {val}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values & Team Stats */}
      <section className="py-20 bg-background border-y border-border">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div className="space-y-2">
              <p className="text-4xl font-heading text-primary font-bold">100%</p>
              <p className="text-sm text-muted-foreground uppercase tracking-wider">Satisfaction Client</p>
            </div>
            <div className="space-y-2">
              <p className="text-4xl font-heading text-primary font-bold">SIPIM</p>
              <p className="text-sm text-muted-foreground uppercase tracking-wider">Ancrage Local</p>
            </div>
            <div className="space-y-2">
              <p className="text-4xl font-heading text-primary font-bold">Luxe</p>
              <p className="text-sm text-muted-foreground uppercase tracking-wider">Standard de Qualité</p>
            </div>
            <div className="space-y-2">
              <p className="text-4xl font-heading text-primary font-bold">2026</p>
              <p className="text-sm text-muted-foreground uppercase tracking-wider">Expertise Certifiée</p>
            </div>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-24 bg-card relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-background rounded-3xl p-8 md:p-12 shadow-xl border border-border flex flex-col md:flex-row gap-12 items-center">
            <div className="flex-1 space-y-6">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
                <MapPin className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-3xl font-heading text-foreground">Situé à la SIPIM Port-Bouët</h2>
              <p className="text-muted-foreground leading-relaxed">
                Notre siège social est stratégiquement situé à la SIPIM Port-Bouët, nous permettant d'être au plus près des pôles de développement de la ville d'Abidjan et des zones balnéaires comme Bassam et Assini.
              </p>
              <div className="pt-4">
                <p className="font-bold text-foreground">{CONTACT_INFO.address}</p>
                <p className="text-muted-foreground">{CONTACT_INFO.city}</p>
              </div>
            </div>
            <div className="flex-1 w-full">
              <div className="aspect-video rounded-2xl bg-muted flex items-center justify-center border border-dashed border-border">
                <div className="text-center p-6">
                  <MapPin className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                  <p className="text-sm text-muted-foreground italic">Plan de localisation SIPIM Port-Bouët</p>
                  <p className="text-xs text-muted-foreground mt-2">Visitez-nous dans nos bureaux pour une consultation personnalisée.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <Users className="w-12 h-12 text-primary mx-auto mb-6" />
          <h2 className="text-3xl font-heading mb-8">Notre Philosophie de Service</h2>
          <p className="text-lg text-muted-foreground italic">
            "Chez YKE Multi_Services, nous ne vendons pas seulement des produits ou des terrains. Nous construisons le cadre de vie de vos rêves et nous célébrons vos moments les plus précieux avec élégance."
          </p>
          <div className="mt-12 flex justify-center gap-6">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-primary" />
              <span className="text-sm font-medium">Sécurité Garantie</span>
            </div>
            <div className="flex items-center gap-2">
              <Award className="w-5 h-5 text-primary" />
              <span className="text-sm font-medium">Qualité Premium</span>
            </div>
          </div>
        </div>
      </section>

      {/* Final Call to Action */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div 
            whileHover={{ scale: 1.01 }}
            className="bg-primary rounded-[2rem] p-12 text-primary-foreground text-center relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -mr-32 -mt-32"></div>
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-heading mb-6">Prêt à collaborer avec nous ?</h2>
              <p className="text-xl opacity-90 mb-10 max-w-2xl mx-auto">
                Que ce soit pour une bouteille d'exception, une résidence de rêve ou un investissement foncier, nous sommes là pour vous.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a 
                  href="/contact" 
                  className="bg-background text-foreground px-8 py-4 rounded-full font-bold hover:shadow-lg transition-all active:scale-95"
                >
                  Contactez-nous aujourd'hui
                </a>
                <a 
                  href="/services" 
                  className="bg-primary-foreground/20 backdrop-blur-md border border-white/30 text-white px-8 py-4 rounded-full font-bold hover:bg-white/20 transition-all active:scale-95"
                >
                  Voir nos services
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
